import * as React from "react";
import PropTypes from "prop-types";
import {useTheme} from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridToolbarExport
} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  experimentalStyled,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {FirstPage, LastPage, NavigateBefore, NavigateNext} from "@mui/icons-material";
import {GET, PATCH, BASE_URL} from "APIclient";
import Snackbar, {SnackbarCloseReason} from "@mui/material/Snackbar";
import dayjs from "dayjs";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import {Dialog, DialogTitle, DialogContent, DialogActions} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {InsertLink, PictureAsPdf} from "@mui/icons-material";
import PrintIcon from "@mui/icons-material/Print";
import {useNavigate} from "react-router-dom";
import IssueAppoinmentLetter from "./IssueAppoinmentLetter";
import axios from "axios";
import ReplayIcon from "@mui/icons-material/Replay";
import properties from "constants/ApplicationConstants";
import {formatDesignation} from "constants/ApplicationConstants";
import copyableCell from "components/CopyableCell";

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <Typography variant="caption" sx={{color: "#666"}}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={handleFirstPage} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton onClick={handlePreviousPage} disabled={page === 0 || loading} size="small">
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function OfferList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [value, setValue] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  let [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState(null);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("error");
  const [page, setPage] = React.useState(0);
  const [pageSize, setSize] = React.useState(20);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 20});
  const [employeeName, setEmployeeName] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  // Updated fetchData with logging and fallback
  const fetchData = async (page, pageSize, employeeName = "", status = "") => {
    try {
      setLoading(true);
      const response = await GET(
        `admin/offer/index?page=${page}&size=${pageSize}&search=${employeeName}&status=${status}`
      );
      console.log("API Response:", response);
      if (response.status === 200) {
        const data = response?.data?.data?.paginatedData || [];
        const totalRecords = response?.data?.data?.totalItems || 0;
        const totalPages = response?.data?.data?.totalPages || 0;

        const formattedData = data.map((item, index) => ({
          id: page * pageSize + index + 1,
          offerId: item?.offerId,
          offerDate: item?.offerDate,
          employeeName: item?.employeeName,
          emailAddress: item?.emailAddress,
          mobileNo: item?.mobileNo,
          designation: formatDesignation(item?.designation),
          joinDate: item?.joinDate,
          status: item?.status
        }));

        setData(formattedData);
        setTotalRows(totalRecords);
        setTotalPages(totalPages);
        console.log("Total Rows:", totalRecords, "Total Pages:", totalPages);
      } else {
        console.error("Unexpected response status:", response.status);
        setData([]);
        setTotalRows(0);
        setTotalPages(0);
        setSnackbar({children: "Failed to fetch data", severity: "error"});
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalRows(0);
      setTotalPages(0);
      setSnackbar({children: "Error fetching data", severity: "error"});
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData(page, pageSize, employeeName, status);
  }, []);

  const handleSearch = async () => {
    try {
      setPage(0);
      setPaginationModel({page: 0, pageSize});
      setLoading(true);
      const response = await GET(
        `admin/offer/index?page=${page}&size=${pageSize}&search=${employeeName}&status=${status}`
      );
      console.log("Search API Response:", response);
      const data = response?.data?.data?.paginatedData || [];
      const totalRecords = response?.data?.data?.totalItems || 0;
      const totalPages = response?.data?.data?.totalPages || 0;

      const formattedData = data.map((item, index) => ({
        id: index + 1,
        offerId: item?.offerId,
        offerDate: item?.offerDate,
        employeeName: item?.employeeName,
        emailAddress: item?.emailAddress,
        mobileNo: item?.mobileNo,
        designation: formatDesignation(item?.designation),
        joinDate: item?.joinDate,
        status: item?.status
      }));

      setData(formattedData);
      setTotalRows(totalRecords);
      setTotalPages(totalPages);
      console.log("Search Total Rows:", totalRecords, "Search Total Pages:", totalPages);
    } catch (error) {
      console.error("Error fetching search data:", error);
      setData([]);
      setTotalRows(0);
      setTotalPages(0);
      setSnackbar({children: "Error fetching search data", severity: "error"});
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = paginationModel => {
    const newPage = paginationModel.page;
    const newPageSize = paginationModel.pageSize;
    console.log("Pagination Change:", {newPage, newPageSize});
    setPage(newPage);
    setSize(newPageSize);
    setPaginationModel(paginationModel);
    fetchData(newPage, newPageSize, employeeName, status);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    fetchData(newPage, pageSize, employeeName, status);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      await fetchData(lastPageIndex, pageSize, employeeName, status);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };
  const handleProcessRowUpdateError = React.useCallback(error => {
    setSnackbar({children: error.message, severity: "error"});
  }, []);
  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };

  const handleView = params => () => {
    // navigate(`/admin/offer/${params.row.id}`
  };
  const handleEditClick = params => () => {
    setRowModesModel({...rowModesModel, [params.offerId]: {mode: GridRowModes.Edit}});
    navigate(`/editOffer`, {state: params.offerId});
  };

  const handleViewClick = params => () => {
    setRowModesModel(prevState => ({
      ...prevState,
      [params.offerId]: {mode: GridRowModes.Edit}
    }));
    navigate("/viewOffer", {state: params.offerId});
  };

  const handleIssueAppoinment = params => () => {
    setRowModesModel({...rowModesModel, [params.offerId]: {mode: GridRowModes.Edit}});
    navigate(`/IssueAppoinmentLetter`, {state: params.offerId});
  };
  const handleReject = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    navigate(`/CancelOfferLetter`, {state: id});
  };

  const handleRevokeOffer = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    navigate(`/revokeOfferLetter`, {state: id});
  };

  const handleSaveClick = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
  };
  const handleClose = () => {
    setOpen(false);
  };

  const processRowUpdate = newRow => {
    const updatedRow = {...newRow, isNew: false};
    setData(data => data.map(row => (row.offerId === updatedRow.id ? updatedRow : row)));
    return updatedRow;
  };

  function formatDate(dateString) {
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).format(date);
  }

  const columns = [
    {field: "id", headerName: "#", width: 20, headerAlign: "center", align: "center"},
    {field: "offerId", headerName: "ID", width: 80, hide: true},
    {
      field: "offerDate",
      headerName: "Offer Date",
      width: 105,
      renderCell: params => {
        const formattedDate = formatDate(params.value);
        return formattedDate || "N/A";
      }
    },
    {
      field: "employeeName",
      headerName: "Candidate Name",
      width: 130,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "emailAddress",
      headerName: "Email ID",
      width: 180,
      renderCell: copyableCell
    },
    {
      field: "mobileNo",
      headerName: "Mobile",
      width: 100,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 140,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "joinDate",
      headerName: "Joining Date",
      width: 105,
      align: "left",
      headerAlign: "left",
      renderCell: params => {
        const formattedDate = formatDate(params.value);
        return formattedDate || "N/A";
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: params => {
        const status = params.row.status;
        if (status === 0) {
          return [
            <GridActionsCellItem
              key={`View-${params.row.offerId}`}
              icon={
                <Tooltip title="View">
                  <VisibilityIcon />
                </Tooltip>
              }
              label="View"
              color="primary"
              onClick={handleViewClick(params.row)}
            />,
            <GridActionsCellItem
              key={`Revoke-${params.row.offerId}`}
              icon={
                <Tooltip title="Revoke Offer Letter">
                  <ReplayIcon />
                </Tooltip>
              }
              label="Revoke"
              color="error"
              onClick={handleRevokeOffer(params.row.offerId)}
            />
          ];
        } else {
          return [
            <GridActionsCellItem
              key={`View-${params.row}`}
              icon={
                <Tooltip title="View" arrow>
                  <VisibilityIcon />
                </Tooltip>
              }
              label="View"
              color="primary"
              onClick={handleViewClick(params.row)}
            />,
            <GridActionsCellItem
              key={`Edit-${params.row}`}
              icon={
                <Tooltip title="Edit" arrow>
                  <EditIcon />
                </Tooltip>
              }
              label="Approve"
              color="info"
              onClick={handleEditClick(params.row)}
            />,
            <GridActionsCellItem
              key={`Issue-${params.row}`}
              icon={
                <Tooltip title="Issue Appointment" arrow>
                  <InsertLink />
                </Tooltip>
              }
              label="Issue Appointment"
              color="warning"
              onClick={handleIssueAppoinment(params.row)}
            />,
            <GridActionsCellItem
              key={`Print-${params.row}`}
              icon={
                <Tooltip title="Print" arrow>
                  <PrintIcon />
                </Tooltip>
              }
              label="print"
              color="success"
              onClick={() => handlePrintOfferLetter(params.row)}
            />,
            <GridActionsCellItem
              key={`download-${params.id}`}
              icon={
                <Tooltip title="Download" arrow>
                  <PictureAsPdf />
                </Tooltip>
              }
              label="PDF"
              color="primary"
              onClick={() => downloadOfferLetter(params.row)}
            />,
            <GridActionsCellItem
              key={`cancel-${params.getRowId}`}
              icon={
                <Tooltip title="Cancel" arrow>
                  <CancelIcon />
                </Tooltip>
              }
              label="Cancel"
              color="error"
              onClick={handleReject(params.id)}
            />
          ];
        }
      }
    }
  ];

  const onSearch = e => {
    let {value, name} = e.target;
    if (name === "employeeName") {
      setEmployeeName(value);
    } else if (name === "status") {
      setStatus(value ? value : "");
    }
  };

  const handlePrintOfferLetter = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/downloadCertificate?type=offer_letter&search=${row.offerId}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });
      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }
      const fileBlob = new Blob([response.data], {type: "application/pdf"});
      const fileUrl = URL.createObjectURL(fileBlob);
      const newWindow = window.open(fileUrl, "_blank");
      if (!newWindow) {
        alert("Please allow pop-ups to view the offer letter.");
      }
    } catch (error) {
      console.error("Error fetching offer letter:", error);
      alert("Error generating the offer letter. Please try again.");
    }
  };

  const downloadOfferLetter = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/printCertificate?type=offer_letter&search=${row.offerId}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });
      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }
      const fileBlob = new Blob([response.data], {type: "application/pdf"});
      const fileUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = fileUrl;
      const empName = row.employeeName
        .replace(/[^a-zA-Z0-9-_]/g, "_")
        .replace(/_+/g, "_")
        .trim();
      link.download = `${empName}_Offer_Letter.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating offer letter:", error);
      alert("Error generating the offer letter. Please try again.");
    }
  };

  const viewOfferLetterDetails = row => {
    navigate(`/view-offer/${row.offerId}`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <AppBar position="static">
        <Card>
          <MDBox container pt={2} pb={2} px={3}>
            <MDBox
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(12, 1fr)",
                gap: 1,
                alignItems: "center"
              }}
            >
              <Typography
                sx={{
                  gridColumn: "span 2",
                  color: "textPrimary",
                  fontFamily: properties.fontFamily,
                  fontSize: "15px",
                  fontWeight: "bold"
                }}
              >
                Offer List
              </Typography>
              <MDInput
                sx={{gridColumn: "span 3"}}
                value={employeeName}
                onChange={e => {
                  onSearch(e);
                }}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    handleSearch();
                  }
                }}
                name="employeeName"
                label="Search Candidate"
                variant="outlined"
              />
              <FormControl variant="outlined" sx={{m: 0, width: "20ch", gridColumn: "span 3"}}>
                <InputLabel id="Status">Status</InputLabel>
                <Select
                  labelId="status"
                  id="status"
                  name="status"
                  value={status}
                  onChange={e => onSearch(e)}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  label="Status"
                  sx={{minWidth: 150, p: 1.5}}
                >
                  <MenuItem value="1">Opened</MenuItem>
                  <MenuItem value="0">Denied</MenuItem>
                </Select>
              </FormControl>
              <MDButton
                sx={{gridColumn: "span 2", background: properties.colors.bgcolorbluethem}}
                variant="gradient"
                color="info"
                type="button"
                onClick={handleSearch}
              >
                Search
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </AppBar>
      <MDBox sx={{overflow: "auto"}}>
        <Paper>
          <DataGrid
            rows={data}
            columns={columns}
            rowModesModel={rowModesModel}
            getRowId={row => row.offerId}
            columnVisibilityModel={{
              offerId: false
            }}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            loading={loading}
            paginationMode="server"
            rowCount={totalRows}
            pageSizeOptions={[4]}
            paginationModel={paginationModel}
            // onPaginationModelChange={handlePaginationChange}
            hideFooter
            sx={{
              fontFamily: properties.fontFamily,
              fontSize: "14px",
              fontWeight: 400,
              height: "450px",
              "& .MuiDataGrid-scrollbar": {
                width: "10px !important"
              }
            }}
          />
          <CustomPagination
            page={page}
            pageSize={pageSize}
            totalRows={totalRows}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onGoToLastPage={handleGoToLastPage}
            loading={loading}
          />
        </Paper>
      </MDBox>
      <Snackbar
        open={open}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
        autoHideDuration={4000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          close={handleClose}
          severity={snakColor == "success" ? "success" : "error"}
          variant="filled"
          sx={{width: "100%"}}
        >
          {snakMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
